import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lightbulb, RefreshCw, Sparkles, ThumbsUp } from "lucide-react";
import { api } from "@/lib/api";
import { relTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { EmptyState, Pill, Section, Spinner } from "@/components/common";

const priorityTone = (p) => (p === "high" ? "red" : p === "low" ? "blue" : "amber");

export function CoachingThemes({ agentId, rangeParams, analyzed }) {
  const qc = useQueryClient();
  const key = ["agent-themes", agentId, rangeParams];
  const q = useQuery({ queryKey: key, queryFn: () => api.get(`/agents/${agentId}/themes`, { params: rangeParams }).then((r) => r.data), enabled: analyzed >= 3, staleTime: 5 * 60 * 1000 });
  const refresh = async () => {
    const r = await api.get(`/agents/${agentId}/themes`, { params: { ...rangeParams, refresh: true } });
    qc.setQueryData(key, r.data);
  };
  const d = q.data;
  return (
    <Section
      title="Recurring coaching themes"
      eyebrow="GPT-5.4 summary of the coaching notes"
      testId="scorecard-themes"
      className="border-violet-500/30"
      actions={d?.status === "ready" && <Button data-testid="themes-refresh-button" size="sm" variant="ghost" className="h-7 text-xs text-slate-400" onClick={refresh} disabled={q.isFetching}><RefreshCw className="h-3 w-3 mr-1" /> Refresh</Button>}
    >
      {analyzed < 3 ? (
        <EmptyState icon={Sparkles} title="Not enough reviewed calls yet" hint="Themes are generated once at least 3 calls have been scored in the selected range." testId="themes-insufficient" />
      ) : q.isLoading || q.isFetching ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-6 justify-center" data-testid="themes-loading"><Spinner /> GPT-5.4 is reading {analyzed} coaching notes…</div>
      ) : q.isError || !d || d.status !== "ready" ? (
        <EmptyState icon={Sparkles} title="Themes unavailable" hint="Try again in a moment." testId="themes-error" />
      ) : (
        <div className="space-y-4">
          {d.focus && (
            <div className="panel p-3 border-violet-500/40 bg-violet-500/5" data-testid="themes-focus">
              <div className="eyebrow text-violet-300 mb-1 flex items-center gap-1.5"><Lightbulb className="h-3.5 w-3.5" /> Focus for next week</div>
              <p className="text-sm text-slate-100 leading-relaxed">{d.focus}</p>
            </div>
          )}
          <ol className="space-y-3" data-testid="themes-list">
            {d.themes.map((t, i) => (
              <li key={i} className="pl-3 border-l-2 border-[#2d3d58]" data-testid={`theme-${i}`}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-semibold text-slate-100">{t.title}</span>
                  <Pill tone={priorityTone(t.priority)}>{t.priority}</Pill>
                  {t.calls != null && <span className="text-[11px] font-mono text-slate-500">{t.calls} of {d.calls} calls</span>}
                </div>
                <p className="text-xs text-slate-300 leading-relaxed mt-1">{t.detail}</p>
              </li>
            ))}
          </ol>
          {d.strengths?.length > 0 && (
            <div data-testid="themes-strengths">
              <div className="eyebrow text-emerald-400 mb-1 flex items-center gap-1.5"><ThumbsUp className="h-3.5 w-3.5" /> Consistent strengths</div>
              <ul className="space-y-1">{d.strengths.map((s, i) => <li key={i} className="text-xs text-slate-300 pl-2 border-l border-emerald-500/40">{s}</li>)}</ul>
            </div>
          )}
          <div className="text-[10px] text-slate-500 font-mono">Generated {relTime(d.generated_at)} from {d.calls} reviewed calls · refreshes daily</div>
        </div>
      )}
    </Section>
  );
}
